import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import { Filter } from '$shared/utils/filter/components';

import { useProduct } from '$shared/utils/product';
import { useFilterQuery } from '$shared/utils/filter/service/useFilterQuery';
import { ModuleContainer } from "$templates/blocks/module-container";
import { FilterProductList } from "$templates/blocks/m170-filter/components/filter-product-list";
import { DynamicFacets } from "$templates/blocks/m170-filter/components/dynamic-facets";
import {
    MobileFacetsContainer,
    ProductFilterGrid,
    StyledFilterColumn,
    StyledMobileFilterCta,
    StyledMobileFilterCtas,
    StyledProductColumn,
} from "$templates/blocks/m170-filter/style";
import ResetFilterButton from "$shared/components/facets/reset-filter-button/facet-search-bar";
import { FILTER_HOLDER_KEY, FILTER_INSERT_KEY } from "$utils/filter/service/constants";
import {
    parseProductContent
} from "$templates/pages/components/P50ProductDetailPage/components/pdp-tabs/pdp-relationships/product-data-parser";
import {IContentData} from "~/lib/data-contract";
import {
    StyledAccordionHeader,
    StyledHierarchyTree
} from "$templates/pages/components/P10SearchPage/search-products-tab/search-products-tab-styles";
import { StyledHierarchySelection } from '$features/search/hierarchy-tree/hierarchy-tree-styles';
import {Icon} from "$shared/components/icon";
import {Accordion} from "$shared/components/accordion";
import {findNodeById} from "$features/search/hierarchy-tree/hierarchy-tree-utils";
import {useSearchHierarchyFromFilters} from "$utils/search/hooks/use-search-hierarchy";
import {breakpointSizes} from "$theme";
import {useMedia} from "react-use";
import { SidePanel } from '$shared/components/side-panel/side-panel';
import {FilterHeader} from "$templates/blocks/m170-filter/components/filter-header";

export const PdpRelationshipsFilter: FC<{ relationshipKey: string; }> = ({relationshipKey}) => {
    const {productData, originalItemNumber} = useProduct();
    const [loading, setLoading] = useState(false);
    const appliedFiltersRef = useRef<{ [key: string]: string[] }>({});
    const [facetValues, setFacetValues] = useState<string[]>([]);
    const prevFacetValuesRef = useRef<string[]>([]);
    const [selectedHierarchyId, setSelectedHierarchyId] = useState<string | undefined>(undefined);

    const hierarchySearchKey =
        relationshipKey === FILTER_HOLDER_KEY ? FILTER_INSERT_KEY : FILTER_HOLDER_KEY;

    const { rootNodes: hierarchyNodes } = useSearchHierarchyFromFilters({
        userFilters: facetValues.map(v => {
            const parts = v.split('..');
            const reversed = parts.length === 2 ? `${parts[1]}..${parts[0]}` : v;
            return `${hierarchySearchKey}↕${reversed}`;
        }),
    });

    const ACCORDION_ID = 'categories';
    const [showCategories, setShowCategories] = useState<string>(ACCORDION_ID);
    const selectedNode = useMemo(
        () => findNodeById(hierarchyNodes, selectedHierarchyId),
        [hierarchyNodes, selectedHierarchyId]
    );

    const isDesktop = useMedia(`(min-width: ${breakpointSizes.frame}px)`, false);
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    useEffect(() => {
        if (productData) {
            setLoading(true);
            setFacetValues([]);

            const parsedProductData = productData.map(product => ({
                ...product,
                parsedContent: parseProductContent(product.content),
            }));

            const productWithKey = parsedProductData.find(
                (product) =>
                    product.parsedContent &&
                    typeof product.parsedContent === 'object' &&
                    relationshipKey in product.parsedContent
            );

            const parsedContent = productWithKey?.parsedContent as { [key: string]: IContentData[] };
            if (parsedContent?.[relationshipKey] && Array.isArray(parsedContent[relationshipKey])) {
                const computedValues = parsedContent[relationshipKey].flatMap((entry) => entry.values || []);
                setFacetValues(computedValues);
            } else {
                setFacetValues([]);
            }

            setLoading(false);
        }
    }, [productData, relationshipKey]);

    return (
        <ModuleContainer id={`relationships-${originalItemNumber}`} fullWidth>
            <Filter
                filterDataExchanger={useFilterQuery}
                filterScopeId={originalItemNumber || ""}
                {...(selectedHierarchyId ? { hierarchyNode: selectedHierarchyId } : {})}
            >
                {({ resetFilter, setFacets, isLoading }) => {
                    const [didReset, setDidReset] = useState(false);
                    const [didSetFacets, setDidSetFacets] = useState(false);

                    useEffect(() => {
                        setDidReset(false);
                        setDidSetFacets(false);
                    }, [relationshipKey, originalItemNumber]);

                    useEffect(() => {
                        if (facetValues.length === 0) return;
                        const searchKey = relationshipKey === FILTER_HOLDER_KEY ? FILTER_INSERT_KEY : FILTER_HOLDER_KEY;

                        if (!didReset) {
                            resetFilter && resetFilter();
                            setDidReset(true);
                            return;
                        }
                        const facetValuesChanged = JSON.stringify(prevFacetValuesRef.current) !== JSON.stringify(facetValues);

                        if (!didSetFacets || facetValuesChanged) {
                            const facetOptions = facetValues.map((value) => `${searchKey}↕${value}`);
                            setFacets &&
                            setFacets([
                                {
                                    facet: searchKey,
                                    options: facetOptions,
                                    overwrite: false,
                                },
                            ]);
                            appliedFiltersRef.current[searchKey] = facetOptions;
                            setDidSetFacets(true);
                            prevFacetValuesRef.current = facetValues;
                        }
                    }, [relationshipKey, facetValues, didReset, didSetFacets, resetFilter, setFacets]);

                    return (
                        <>
                            <FilterHeader
                                title={
                                    facetValues.length
                                        ? `${relationshipKey === FILTER_HOLDER_KEY ? 'Holder Keys' : 'Insert Keys'}: ${facetValues.join(', ')}`
                                        : `${relationshipKey === FILTER_HOLDER_KEY ? 'Holder Keys' : 'Insert Keys'}: No values found`
                                }
                                onFiltersToggled={!isDesktop ? () => setShowMobileFilters(true) : undefined}
                            />
                            <ProductFilterGrid>
                                {isDesktop && (
                                    <StyledFilterColumn>
                                        <Accordion
                                            value={showCategories}
                                            header={
                                                <StyledAccordionHeader>
                                                    Categories
                                                    {!!selectedHierarchyId && (
                                                        <StyledHierarchySelection>
                                                            {` / ${selectedNode?.label}`}
                                                        </StyledHierarchySelection>
                                                    )}
                                                </StyledAccordionHeader>
                                            }
                                            icon={<Icon icon="chevronUp" size={15} />}
                                            onValueChange={setShowCategories}
                                            overrideId={ACCORDION_ID}
                                        >
                                            <div style={{ padding: '1rem' }}>
                                                <StyledHierarchyTree
                                                    isDesktop={true}
                                                    hierarchyId={originalItemNumber}
                                                    nodes={hierarchyNodes || []}
                                                    selection={selectedHierarchyId}
                                                    onSelectionChanged={(id) => {
                                                        setSelectedHierarchyId(id);
                                                        if (id) {
                                                            setShowCategories('');
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </Accordion>

                                        {!!selectedHierarchyId && <ResetFilterButton handleClick={() => {
                                            if (resetFilter) {
                                                setDidReset(false);
                                                setDidSetFacets(false);
                                                resetFilter();
                                            }
                                        }} />}

                                        {!!selectedHierarchyId && <DynamicFacets />}
                                    </StyledFilterColumn>
                                )}

                                <StyledProductColumn>
                                    {loading ? <p>Loading...</p> : <FilterProductList id={originalItemNumber || ''} />}
                                </StyledProductColumn>
                            </ProductFilterGrid>

                            {!isDesktop && (
                                <SidePanel
                                    show={showMobileFilters}
                                    onChange={setShowMobileFilters}
                                    maxWidth={414}
                                    maxContentHeight={'calc(100vh - 160px)'}
                                    title="Filter"
                                >
                                    <MobileFacetsContainer>
                                        <StyledHierarchyTree
                                            isDesktop={false}
                                            hierarchyId={originalItemNumber}
                                            nodes={hierarchyNodes || []}
                                            selection={selectedHierarchyId}
                                            onSelectionChanged={(id) => {
                                                setSelectedHierarchyId(id);
                                                if (id) {
                                                    setShowMobileFilters(false); // Optional: auto-close when selecting
                                                }
                                            }}
                                        />

                                        <DynamicFacets />
                                    </MobileFacetsContainer>

                                    <StyledMobileFilterCtas>
                                        <StyledMobileFilterCta
                                            onClick={resetFilter}
                                            variant="secondary"
                                        >
                                            Clear Filters
                                        </StyledMobileFilterCta>
                                        <StyledMobileFilterCta
                                            onClick={() => setShowMobileFilters(false)}
                                            isLoading={isLoading}
                                            disabled={false}
                                            variant="primary"
                                        >
                                            Show Results
                                        </StyledMobileFilterCta>
                                    </StyledMobileFilterCtas>
                                </SidePanel>
                            )}
                        </>
                    );
                }}
            </Filter>
        </ModuleContainer>
    );
};